import { useState, useEffect, memo } from 'react';
import { SiteAsideStyled } from 'components/styles/site-aside-styled';

import LorelLocale from 'locale/en/lorel.json';
import MiscLocale from 'locale/en/misc.json';
import SustainabilityLocale from 'locale/en/sustainability.json';
import Head from 'next/head';
import SiteSections from 'components/site/site_sections';
import SiteFilter from 'components/site/site-filter';

import SitePopup from 'components/site/site-popup';
import { RootState } from 'entities/rootState';
import Sticky from 'sticky-js';
import { createGlobalStyle } from 'styled-components';
import {
  PageNewSustainability,
  PageReference,
} from 'components/styles/reference';
import { isScreenLarge } from 'lib/utils/screen-size';
import {
  ContentSection,
  getContentSection,
} from 'lib/contentful/get-content-section';
import { getContentFieldClass } from 'lib/contentful/get-content-field-class';
import { scrollFilterBy } from 'lib/utils/scroll-filter-by';
import { pageReplaced } from 'lib/utils/page-replaced';
import { getContentfulDataDirect } from 'lib/contentful/get-contentful-data-direct';
import { mqLargeMin } from 'styled/tools/media-queries';

const GlobalStyleSustainability = createGlobalStyle`
  ${PageReference}
  ${PageNewSustainability}

  &.with-scrolled header {
    opacity: 0;
    @media ${mqLargeMin} {
      opacity: 1;
    }
  }
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-new-sustainability',
  page_name: SustainabilityLocale.page_name,
  page_title: SustainabilityLocale.page_title,
  page_description: SustainabilityLocale.page_desc,
};
const PageHead = () => {
  const url = 'https://seed.com/sustainability';
  return (
    <Head>
      <title>{`${MiscLocale.brand} • ${PageData.page_title}`}</title>
      <meta name='description' content={PageData.page_description} />
      <link rel='canonical' href={url} />
    </Head>
  );
};

const PagePopupAlgaePaper = memo(() => (
  <>
    <div className='head'>
      <p>Sustainability</p>
      <span>Biodegradable Algae Paper</span>
    </div>
    <div className='body'>
      <div className='blurb'>
        <p>{LorelLocale.lg}</p>
      </div>
    </div>
  </>
));

const PagePopupPaperFoam = memo(() => (
  <>
    <div className='head'>
      <p>Sustainability</p>
      <span>Biodegradable Paper Foam</span>
    </div>
    <div className='body'>
      <div className='blurb'>
        <p>{LorelLocale.lg}</p>
      </div>
    </div>
  </>
));

const PagePopupProtectionPouch = memo(() => (
  <>
    <div className='head'>
      <p>Sustainability</p>
      <span>Biofilm Refill Protection Pouch</span>
    </div>
    <div className='body'>
      <div className='blurb'>
        <p>{LorelLocale.lg}</p>
      </div>
    </div>
  </>
));
const PagePopupBarrierFoam = memo(() => (
  <>
    <div className='head'>
      <p>Sustainability</p>
      <span>Dissolvable Corn-Based Barrier Foam</span>
    </div>
    <div className='body'>
      <div className='blurb'>
        <p>{LorelLocale.lg}</p>
      </div>
    </div>
  </>
));

const PageAside = (props) => {
  const {
    stickyMargin,
    handleFilterBy,
    handleFilterList,
    filter,
    isScreenLarge: isScreenLargeBoolean,
    pageContent,
  } = props;

  return (
    <SiteAsideStyled
      className='site-aside'
      data-sticky-for={1024}
      data-margin-top={stickyMargin}
    >
      <div className='head'>
        <div className='title'>
          <h1>{SustainabilityLocale.page_title}</h1>
        </div>
      </div>

      <div className='body'>
        <SiteFilter
          overlay
          title={SustainabilityLocale.page_title}
          activeFilter={filter}
          handleFilter={handleFilterBy}
          list={handleFilterList(pageContent)}
        />

        {!isScreenLargeBoolean && (
          <div className='filter-mobile'>
            <SiteFilter
              title={SustainabilityLocale.page_title}
              activeFilter={filter}
              handleFilter={handleFilterBy}
              list={handleFilterList(pageContent)}
            />
          </div>
        )}
      </div>
    </SiteAsideStyled>
  );
};

const HeroSection = (props) => {
  const { data, onVisible } = props;
  return (
    <div className='hero-section'>
      <SiteSections
        location='Sustainability'
        data={{ heroSection: data } as any}
        onVisible={onVisible}
      />
    </div>
  );
};

type PageSustainabilityProps = {
  pageContent: any;
  page_title: string;
  page_description: string;
} & RootState;

type PageSustainabilityState = {
  sticky?: any;
  stickyMargin: number;
  filter?: string;
  isScreenLargeBoolean?: boolean; // Have to specify boolean type because isScreenLarge is also a funciton imported from /utils
};

const PageSustainability = (props: PageSustainabilityProps) => {
  const { pageContent, page_title } = props;
  const [state, setState] = useState<PageSustainabilityState>({
    stickyMargin: 0,
    sticky: null,
    isScreenLargeBoolean: false,
  });

  const handleScroll = () => {
    if (window.scrollY === 0) {
      document.querySelector('html').classList.add('with-header-contrast');
    } else {
      document.querySelector('html').classList.remove('with-header-contrast');
    }
  };

  const handleResize = () => {
    setState({
      ...state,
      isScreenLargeBoolean: isScreenLarge(),
    });
  };

  const handleFilterList = (data) => {
    const sections = getContentSection({ data }) || [];
    const list = sections.reduce((a, v) => {
      const arr = getContentFieldClass(v).split(' ');
      if (arr.indexOf('with-anchor') > -1) {
        return [...a, v.fields.title];
      }
      return [...a];
    }, []);
    return list;
  };

  const handleFilterBy = (v, behavior) => {
    setState({ ...state, filter: v });
    const siteContent = document.querySelector<HTMLElement>('.site-content');
    const target = siteContent.querySelector<HTMLElement>(
      `[data-title="${v}"]`,
    );
    scrollFilterBy({ target, scr: { sm: 90, lg: 50 }, behavior });
  };

  const handleVisible = ({ title }) => {
    setState({
      ...state,
      filter: title,
    });
  };

  useEffect(() => {
    pageReplaced(PageData);
    document.querySelector('html').classList.add('with-header-contrast');
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    const header = document.querySelector<HTMLElement>('header');
    const sticky = new Sticky('.site-aside');

    const stickyMargin = header.offsetHeight + 40;
    setState({
      ...state,
      sticky,
      stickyMargin,
      isScreenLargeBoolean: isScreenLarge(),
      filter: handleFilterList(pageContent)[0],
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      const siteHtml = document.querySelector('html');
      siteHtml.classList.remove('with-header-contrast');
    };
  }, []);

  const { stickyMargin, filter, isScreenLargeBoolean } = state;
  const title = page_title || '';

  const heroSection = pageContent?.heroSection as ContentSection;
  return (
    <>
      <GlobalStyleSustainability />
      <PageHead />
      <>
        {heroSection && (
          <HeroSection data={heroSection} onVisible={handleVisible} />
        )}
        <div className='container'>
          <div className='sections-list' data-sticky-container>
            <PageAside
              title={title}
              filter={filter}
              pageContent={pageContent}
              stickyMargin={stickyMargin}
              isScreenLarge={isScreenLargeBoolean}
              handleFilterBy={handleFilterBy}
              handleFilterList={handleFilterList}
            />
            <SiteSections
              location='Sustainability'
              data={pageContent}
              onVisible={handleVisible}
            />
          </div>
        </div>
      </>
      <SitePopup type='drawer' id='modal-algae-paper'>
        <PagePopupAlgaePaper />
      </SitePopup>

      <SitePopup type='drawer' id='modal-paper-foam'>
        <PagePopupPaperFoam />
      </SitePopup>

      <SitePopup type='drawer' id='modal-protection-pouch'>
        <PagePopupProtectionPouch />
      </SitePopup>

      <SitePopup type='drawer' id='modal-barrier-foam'>
        <PagePopupBarrierFoam />
      </SitePopup>
    </>
  );
};

export async function getStaticProps() {
  const pageSlug = 'new-sustainability';
  const sustainabilityQuery = {
    content_type: 'page',
    'fields.slug': pageSlug,
    include: 10,
  };
  const sustainabilityPayload =
    await getContentfulDataDirect(sustainabilityQuery);

  const props = {
    page_name: pageSlug,
    pageContent: sustainabilityPayload.items?.pop()?.fields || null,
    page_class: 'page-new-sustainability',
  };

  return {
    props,
    revalidate: 60 * 60, // One hour in seconds
  };
}

export default PageSustainability;
